import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { INDUSTRY_PAGES } from "@/data/industries";
import { CONSULTING_SERVICES, SOLUTIONS, SEO_DEFAULTS } from "@/data/content";

export default function IndustryDetail() {
  const { slug } = useParams();
  const industry = useMemo(() => INDUSTRY_PAGES.find((i) => i.slug === slug), [slug]);
  if (!industry) {
    return (
      <div className="pt-40 pb-24 px-6 text-center text-[#3F4A6B]">
        <Seo title="Industry not found" noindex />
        <div className="font-display text-3xl mb-4">Industry not found.</div>
        <Link to="/industries" className="text-gold underline">Return to industries</Link>
      </div>
    );
  }
  const relServices = CONSULTING_SERVICES.filter((s) => industry.services.includes(s.slug));
  const relPlatforms = SOLUTIONS.filter((p) => industry.platforms.includes(p.slug));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `${industry.name} — AlchamisGroup`,
    description: industry.seoDescription,
    url: `${SEO_DEFAULTS.baseUrl}/industries/${industry.slug}`,
    about: industry.name,
  };

  return (
    <div data-testid={`industry-detail-${industry.slug}`} className="pt-32 pb-24">
      <Seo
        title={industry.seoTitle.replace(" — AlchamisGroup", "")}
        description={industry.seoDescription}
        path={`/industries/${industry.slug}`}
        image={industry.hero}
        jsonLd={jsonLd}
      />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Link to="/industries" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#6B7390] hover:text-gold">
          <ArrowLeft size={14} /> All Industries
        </Link>
        <div className="mt-10 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-7">
            <div className="number-badge">{industry.eyebrow}</div>
            <h1 className="font-display text-5xl sm:text-7xl text-[#001B54] mt-6 leading-[1.02]">{industry.headline}</h1>
            <p className="mt-8 text-[#3F4A6B] text-lg leading-relaxed max-w-2xl">{industry.lead}</p>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="aspect-[4/3] overflow-hidden border border-[#001B54]/10">
              <img src={industry.hero} alt={industry.name} className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="mt-20 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <span className="overline">Sector Pressures</span>
          <h2 className="font-display text-4xl text-[#001B54] mt-6 leading-tight">What&apos;s driving boards today.</h2>
        </div>
        <div className="col-span-12 lg:col-span-7 space-y-3">
          {industry.pressures.map((p) => (
            <div key={p} className="border-l-2 border-gold pl-5 py-1.5">
              <p className="text-[#0A1228] leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-20 max-w-[1400px] mx-auto px-6 md:px-12">
        <span className="overline">How We Engage</span>
        <h2 className="font-display text-4xl text-[#001B54] mt-6 leading-tight">Capabilities tuned to {industry.name}.</h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#001B54]/8">
          {industry.capabilities.map((c) => (
            <div key={c} className="bg-white p-7">
              <Check size={16} className="text-gold mb-3" />
              <div className="text-[#0A1228] leading-relaxed">{c}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-20 max-w-[1400px] mx-auto px-6 md:px-12">
        <span className="overline">Outcomes</span>
        <h2 className="font-display text-4xl text-[#001B54] mt-6 leading-tight">Measured at the board level.</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#001B54]/8">
          {industry.outcomes.map((o) => (
            <div key={o.label} className="bg-white p-10 text-center">
              <div className="font-display text-5xl gold-text">{o.value}</div>
              <div className="text-[#3F4A6B] text-sm mt-3 max-w-[200px] mx-auto">{o.label}</div>
            </div>
          ))}
        </div>
      </section>

      {(relServices.length > 0 || relPlatforms.length > 0) && (
        <section className="mt-20 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
          {relServices.length > 0 && (
            <div className="col-span-12 lg:col-span-7">
              <span className="overline">Relevant Practices</span>
              <h2 className="font-display text-3xl text-[#001B54] mt-6 leading-tight">Where we typically begin.</h2>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relServices.map((s) => (
                  <Link to={`/services/${s.slug}`} key={s.slug} className="group border border-[#001B54]/10 hover:border-gold/40 p-5 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="number-badge">{s.code}</div>
                      <ArrowUpRight size={14} className="text-gold opacity-0 group-hover:opacity-100" />
                    </div>
                    <div className="font-display text-xl text-[#001B54] mt-3">{s.name}</div>
                    <div className="text-[#6B7390] text-sm mt-2">{s.summary.slice(0, 110)}…</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {relPlatforms.length > 0 && (
            <div className="col-span-12 lg:col-span-5">
              <span className="overline">Platforms</span>
              <h2 className="font-display text-3xl text-[#001B54] mt-6 leading-tight">Owned by AlchamisGroup.</h2>
              <div className="mt-8 space-y-3">
                {relPlatforms.map((p) => (
                  <Link to={`/platforms/${p.slug}`} key={p.slug} className="group flex items-start gap-4 border border-gold/20 hover:border-gold/60 p-5 transition-colors bg-gold/[0.03]">
                    <div className="number-badge mt-1">{p.ventureCode.split(" — ")[0]}</div>
                    <div className="flex-1">
                      <div className="font-display text-xl text-[#001B54]">{p.name}</div>
                      <div className="text-[#3F4A6B] text-sm mt-1">{p.tagline}</div>
                    </div>
                    <ArrowUpRight size={16} className="text-gold opacity-0 group-hover:opacity-100" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <section className="mt-24 max-w-[1100px] mx-auto px-6 md:px-12 text-center">
        <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] leading-tight">
          Engage AlchamisGroup for {industry.name}.
        </h2>
        <p className="text-[#3F4A6B] mt-6 max-w-xl mx-auto">
          Every engagement begins with a private intake and an AI diagnostic memo.
        </p>
        <div className="mt-8 flex justify-center gap-4 flex-wrap">
          <Link to="/ai-advisor" className="luxury-button">Begin Diagnostic <ArrowRight size={14} /></Link>
          <Link to="/contact" className="luxury-button-ghost">Speak to Partner</Link>
        </div>
      </section>
    </div>
  );
}
